/**
 * Replacement questions for a paper item that misses its section's blueprint.
 *
 * "Align with blueprint" used to have Leo rewrite the question in place, which
 * writes a new question when the bank very likely already holds one that fits.
 * The author's first answer should be a question they can trust: one that
 * exists, has been sat, and has numbers behind it. So the bank is searched
 * first, with the same alignment check the Blueprint column uses, so a "fit"
 * here can never disagree with the column that raised the problem.
 *
 * A new question from Leo is the last answer, offered when nothing in the bank
 * fits every target, and always labelled as new.
 */

import {
  paperQuestionBlueprintExplain,
  type PaperBlueprintCheck,
} from "@/lib/leo-blueprint-section-fill"
import type { AssessmentBankQuestion } from "@/lib/mock/assessment-add-questions"
import { paperBloomFromAuthoring } from "@/lib/question-authoring-seed"

export type ReplacementFit = "full" | "partial"

export type ReplacementCandidate = {
  row: AssessmentBankQuestion
  fit: ReplacementFit
  /** Targets this question meets, by label ("Category", "Bloom's"…). */
  met: string[]
  /** Targets it misses. Empty for a full fit. */
  missed: string[]
  checks: PaperBlueprintCheck[]
}

export type ReplacementSearch = {
  /** Meets every target on the section's blueprint. */
  full: ReplacementCandidate[]
  /** Meets the category and most targets, but not all. Shown only when nothing fits fully. */
  partial: ReplacementCandidate[]
  /** The targets the section asks for, in words. Empty when the section has no blueprint. */
  targets: string[]
}

/** A bank row, in the words the section blueprint checks. */
export function blueprintSubjectOfBankRow(row: AssessmentBankQuestion) {
  return {
    topic: row.topic,
    // The bank says "Remember"; a paper's blueprint says "Remembering".
    blooms: paperBloomFromAuthoring(row.bloom ?? ""),
    type: row.type,
    difficulty: row.difficulty,
  }
}

/** Less exposed first: a never-sat question beats one in heavy rotation. */
function exposure(row: AssessmentBankQuestion): number {
  return row.neverUsed ? 0 : (row.timesUsed ?? 1)
}

export function findBlueprintReplacements(input: {
  sectionId: string
  bank: readonly AssessmentBankQuestion[]
  /** Ids already on the paper, including the question being replaced. */
  excludeIds: ReadonlySet<string>
  limit?: number
}): ReplacementSearch {
  const limit = input.limit ?? 5
  const full: ReplacementCandidate[] = []
  const partial: ReplacementCandidate[] = []
  let targets: string[] = []

  for (const row of input.bank) {
    if (input.excludeIds.has(row.id) || row.status === "Draft") continue
    const explain = paperQuestionBlueprintExplain(input.sectionId, blueprintSubjectOfBankRow(row))
    if (explain.checks.length === 0) return { full: [], partial: [], targets: [] }
    if (targets.length === 0) {
      targets = explain.checks.map(
        (check) => `${check.label}: ${check.plan.join(" or ")}`,
      )
    }
    const met = explain.checks.filter((check) => check.aligned).map((check) => check.label)
    const missed = explain.checks.filter((check) => !check.aligned).map((check) => check.label)
    const candidate: ReplacementCandidate = {
      row,
      fit: explain.aligned ? "full" : "partial",
      met,
      missed,
      checks: explain.checks,
    }
    if (explain.aligned) full.push(candidate)
    // A partial fit has to stay on topic: a different subject is not a
    // replacement, however well it matches the other targets.
    else if (met.includes("Category") && met.length >= missed.length) partial.push(candidate)
  }

  const byBest = (a: ReplacementCandidate, b: ReplacementCandidate) =>
    b.met.length - a.met.length || exposure(a.row) - exposure(b.row)
  return {
    full: full.sort(byBest).slice(0, limit),
    partial: partial.sort(byBest).slice(0, limit),
    targets,
  }
}
